import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap } from '@angular/router';

import { Observable, Subject, filter, map, takeUntil, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';

import { FinnairOrder, PaymentTransaction } from '@fcom/dapi/api/models';
import {
  BookingOrderService,
  CommonBookingFeatureState,
  OrderActions,
  orderData,
  orderStatus,
  OrderStatus,
  paymentIdsPaid,
  paymentStatus,
  PaymentStatus,
} from '@fcom/common-booking';
import { PurchaseSuccessService } from '@fcom/common-booking/modules/purchase-success';
import { pathSegmentsToPathString } from '@fcom/core/utils/app-url-utils';
import { GtmService } from '@fcom/common/gtm';
import { GtmPurchaseFlow } from '@fcom/common/interfaces/gtm.interface';
import { setPaymentIdPaid } from '@fcom/core/actions/global.actions';
import { mapPaymentMethods } from '@fcom/common-booking/utils/payment.utils';
import { LoaderTheme } from '@fcom/ui-components';

@Component({
  selector: 'fin-mmb-purchase-success-route',
  templateUrl: './purchase-success-route.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingPurchaseSuccessRouteComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);
  private purchaseSuccessService = inject(PurchaseSuccessService);
  private bookingOrderService = inject(BookingOrderService);
  private locationService = inject(Location);
  private activatedRoute = inject(ActivatedRoute);
  private gtmService = inject(GtmService);

  @HostBinding('class') classAttribute = 'col-f md-col-3-10';
  readonly LoaderTheme = LoaderTheme;
  readonly OrderStatus = OrderStatus;

  private ngDestroyed$ = new Subject();

  order$: Observable<FinnairOrder>;
  orderStatus$: Observable<OrderStatus>;
  paymentMethods$: Observable<PaymentTransaction[] | undefined>;

  constructor() {
    this.order$ = this.store$.pipe(orderData());
    this.orderStatus$ = this.store$.pipe(orderStatus());
  }

  ngOnInit(): void {
    const paramMap: ParamMap = this.activatedRoute.snapshot.queryParamMap;
    if (this.purchaseSuccessService.hasOrderQueryParams(paramMap)) {
      this.store$.dispatch(
        OrderActions.setOrderFetchInformation({
          orderInformation: {
            id: paramMap.get(this.purchaseSuccessService.PARAM_ORDER_ID),
            hash: paramMap.get(this.purchaseSuccessService.PARAM_HASH),
          },
        })
      );

      // Remove sensitive param from history and browser url
      this.locationService.replaceState(pathSegmentsToPathString(this.activatedRoute.snapshot.pathFromRoot));
    }

    this.queueOrderLoad();

    this.paymentMethods$ = this.order$.pipe(map((order) => mapPaymentMethods(order)));
    this.store$
      .pipe(
        paymentStatus(),
        filter((status) => status === PaymentStatus.VERIFIED),
        withLatestFrom(this.paymentMethods$, this.order$, this.store$.pipe(paymentIdsPaid())),
        filter(([_, __, order, paidIds]) => {
          const paymentId = order.checkouts?.sort((a, b) => +a.timestamp - +b.timestamp)[0]?.id;
          return !paidIds.some((id) => id === paymentId);
        }),
        takeUntil(this.ngDestroyed$)
      )
      .subscribe(([_, paymentMethods, order]) => {
        const paymentId = order.checkouts?.sort((a, b) => +a.timestamp - +b.timestamp)[0]?.id;
        const amount =
          order.prices?.checkouts?.find((c) => c.id === paymentId)?.prices?.services?.total?.totalAmount?.amount ??
          order.prices?.pending?.services?.total?.totalAmount.amount;
        const hasPricesToReport = parseFloat(amount) > 0;
        if (hasPricesToReport) {
          this.gtmService.completeManageBookingPurchase(order, paymentMethods, GtmPurchaseFlow.MANAGE_BOOKING);
          this.store$.dispatch(setPaymentIdPaid({ paymentId }));
        }
      });
  }

  queueOrderLoad(): void {
    this.bookingOrderService.queueOrderLoad(true);
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next(true);
  }
}
